import React from 'react';
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import LoadingCircle from '../LoadingCircle/LoadingCircle';

import './CustomModal.css';

/**
 * Reusable Modal component built on top of react-bootstrap Modal
 * Props:
 * - show: boolean (required)
 * - handleHide: function (required)
 * - handleConfirm?: function (required if singleButton is false)
 * - text?: string | ReactNode
 * - title?: string | ReactNode (default: t('documents.warning'))
 * - children?: ReactNode (rendered inside body, after text)
 * - disabledButton?: boolean (default: false)
 * - singleButton?: boolean (default: false)
 * - closable?: boolean (default: true)
 * - loading?: boolean (default: false)
 * - hideButtons?: boolean (default: false) - hides footer buttons when true
 * - confirmText?: string (default: t('general.confirm'))
 * - cancelText?: string (default: t('general.cancel'))
 * - variant?: 'default' | 'centered' | 'large' | 'small' (default: 'default')
 * - bodyClassName?: string (optional, merged with default classes)
 */
export const CustomModal = ({ 
  show, 
  handleHide, 
  handleConfirm, 
  text, 
  title, 
  children, 
  disabledButton, 
  singleButton = false,
  closable = true,
  loading = false,
  hideButtons = false,
  confirmText,
  cancelText,
  variant = 'default',
  bodyClassName = ''
}) => {
  const { t } = useTranslation();

  // Determine modal size
  const getModalSize = () => {
    if (variant === 'large') return 'lg';
    if (variant === 'small') return 'sm';
    return undefined; // default
  };

  // Determine if modal should be centered
  const isCentered = variant === 'centered';

  // Handler for single button
  const handleSingleButton = () => {
    if (handleConfirm) {
      handleConfirm();
    } else {
      handleClose();
    }
  };

  // Handler to close the modal
  const handleClose = () => {
    if (closable && handleHide) {
      handleHide();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={closable ? handleClose : undefined}
      size={getModalSize()}
      centered={isCentered}
      backdrop={closable ? true : 'static'}
      keyboard={closable}
    >
      <Modal.Header closeButton={closable}>
        <Modal.Title>{title || t('documents.warning')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={`${loading ? 'modal-body-loading' : ''} ${bodyClassName}`}>
        {loading && (
          <div className="modal-loading-container">
            <LoadingCircle />
          </div>
        )}
        {!loading && (
          <>
            {text && <p>{text}</p>}
            {children}
          </>
        )}
      </Modal.Body>
      {!loading && !hideButtons && (
        <Modal.Footer>
          {singleButton ? (
            <div className="centered-buttons-disclaimer">
              <Button
                onClick={handleSingleButton}
                disabled={disabledButton}
                className={handleConfirm ? 'single-button-confirm' : 'single-button-cancel'}
              >
                {handleConfirm 
                  ? (confirmText || t('general.confirm'))
                  : (cancelText || t('general.cancel'))
                }
              </Button>
            </div>
          ) : (
            <div className="centered-buttons-disclaimer">
              <Button
                onClick={handleClose}
              >
                {cancelText || t('general.cancel')}
              </Button>
              {handleConfirm && (
                <Button
                  disabled={disabledButton}
                  onClick={handleConfirm}
                  style={{ marginLeft: 20 }}
                >
                  {confirmText || t('general.confirm')}
                </Button>
              )}
            </div>
          )}
        </Modal.Footer>
      )}
    </Modal>
  )
}